import React from 'react';
import {
  FiMail,
  FiEye,
  FiMousePointer,
  FiAlertCircle,
  FiTrendingUp,
  FiClock,
  FiCalendar,
  FiActivity,
} from 'react-icons/fi';

// Type-cast icons for React compatibility
const FiMailComponent = FiMail as unknown as React.ComponentType<{
  size?: number | string;
  className?: string;
}>;
const FiEyeComponent = FiEye as unknown as React.ComponentType<{
  size?: number | string;
  className?: string;
}>;
const FiMousePointerComponent = FiMousePointer as unknown as React.ComponentType<{
  size?: number | string;
  className?: string;
}>;
const FiAlertCircleComponent = FiAlertCircle as unknown as React.ComponentType<{
  size?: number | string;
  className?: string;
}>;
const FiTrendingUpComponent = FiTrendingUp as unknown as React.ComponentType<{
  size?: number | string;
  className?: string;
}>;
const FiClockComponent = FiClock as unknown as React.ComponentType<{
  size?: number | string;
  className?: string;
}>;
const FiCalendarComponent = FiCalendar as unknown as React.ComponentType<{
  size?: number | string;
  className?: string;
}>;
const FiActivityComponent = FiActivity as unknown as React.ComponentType<{
  size?: number | string;
  className?: string;
}>;

// Interface for engagement metrics data
interface EngagementMetrics {
  deliveredCount: number | null;
  openCount: number | null;
  clickCount: number | null;
  bounceCount: number | null;
  engagementScore: number | null;
  lastActivityAt: string | null;
  lastEmailOpenedAt: string | null;
  lastClickAt: string | null;
}

// Component to display engagement metrics in cards
const AudienceViewTopCards: React.FC<{ metrics: EngagementMetrics }> = ({ metrics }) => {
  // Helper function to format numeric value - show 0 if null or undefined
  const formatNumericValue = (value: number | null | undefined): string => {
    if (value === null || value === undefined) {
      return '0';
    }
    return value.toString();
  };

  // Helper function to format date/time - show N/A if null or undefined
  const formatDateTime = (dateString: string | null | undefined): string => {
    if (!dateString) {
      return 'N/A';
    }
    try {
      const date = new Date(dateString);
      // Check if date is valid
      if (isNaN(date.getTime())) {
        return 'N/A';
      }
      // Format: "Jan 02, 2026, 6:16 PM"
      return date.toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
      });
    } catch {
      return 'N/A';
    }
  };

  // Helper function to get background color based on metric type
  const getBgColor = (metricName: string): string => {
    const colors: { [key: string]: string } = {
      delivered: '#E8F5E9', // Light green
      open: '#E3F2FD', // Light blue
      click: '#F3E5F5', // Light purple
      bounce: '#FBE9E7', // Light orange/red
      engagement: '#E0F7FA', // Light cyan
      lastActivity: '#FFF3E0', // Light orange
      lastEmailOpened: '#E8EAF6', // Light indigo
      lastClick: '#FCE4EC', // Light pink
    };
    return colors[metricName] || '#F5F5F5';
  };

  // Helper function to get icon color based on metric type
  const getIconColor = (metricName: string): string => {
    const colors: { [key: string]: string } = {
      delivered: '#4CAF50', // Green
      open: '#2196F3', // Blue
      click: '#9C27B0', // Purple
      bounce: '#FF9800', // Orange
      engagement: '#00BCD4', // Cyan
      lastActivity: '#FF6F00', // Dark orange
      lastEmailOpened: '#3949AB', // Indigo
      lastClick: '#C2185B', // Pink
    };
    return colors[metricName] || '#757575';
  };

  // Helper function to get icon based on metric type
  const getIcon = (metricName: string) => {
    const icons: { [key: string]: React.ReactNode } = {
      delivered: <FiMailComponent className="text-2xl" size={17} />,
      open: <FiEyeComponent className="text-2xl" size={17} />,
      click: <FiMousePointerComponent className="text-2xl" size={17} />,
      bounce: <FiAlertCircleComponent className="text-2xl" size={17} />,
      engagement: <FiTrendingUpComponent className="text-2xl" size={17} />,
      lastActivity: <FiActivityComponent className="text-2xl" size={17} />,
      lastEmailOpened: <FiCalendarComponent className="text-2xl" size={17} />,
      lastClick: <FiClockComponent className="text-2xl" size={17} />,
    };
    return icons[metricName] || <FiMailComponent className="text-2xl" size={17} />;
  };

  // Helper function to get label for metric
  const getLabel = (metricName: string): string => {
    const labels: { [key: string]: string } = {
      delivered: 'Delivered',
      open: 'Opened',
      click: 'Clicked',
      bounce: 'Bounced',
      engagement: 'Engagement Score',
      lastActivity: 'Last Activity',
      lastEmailOpened: 'Last Email Opened',
      lastClick: 'Last Click',
    };
    return labels[metricName] || metricName;
  };

  // Prepare numeric metrics data for rendering (these will show 0 instead of N/A)
  const numericMetricsData = [
    {
      key: 'delivered',
      value: metrics.deliveredCount,
      label: getLabel('delivered'),
      isDate: false,
    },
    {
      key: 'open',
      value: metrics.openCount,
      label: getLabel('open'),
      isDate: false,
    },
    {
      key: 'click',
      value: metrics.clickCount,
      label: getLabel('click'),
      isDate: false,
    },
    {
      key: 'bounce',
      value: metrics.bounceCount,
      label: getLabel('bounce'),
      isDate: false,
    },
    {
      key: 'engagement',
      value: metrics.engagementScore,
      label: getLabel('engagement'),
      isDate: false,
    },
  ];

  // Prepare date metrics for rendering (these will show N/A)
  const dateMetricsData = [
    {
      key: 'lastActivity',
      value: metrics.lastActivityAt,
      label: getLabel('lastActivity'),
    },
    {
      key: 'lastEmailOpened',
      value: metrics.lastEmailOpenedAt,
      label: getLabel('lastEmailOpened'),
    },
    {
      key: 'lastClick',
      value: metrics.lastClickAt,
      label: getLabel('lastClick'),
    },
  ];

  return (
    <>
      {/* Grid layout for 5 cards - Engagement Metrics */}
      <div className='grid grid-cols-5 gap-4 mt-6'>
        {numericMetricsData.map((metric) => (
          <div
            key={metric.key}
            className='bg-white rounded-lg p-4 flex items-center justify-between border border-gray-200 shadow-sm hover:shadow-md transition-shadow duration-200'
          >
            <div className="flex-1">
              <h3
                className='text-gray-500 text-sm mb-2 font-semibold'
                style={{ color: '#657584', fontSize: '14px' }}
              >
                {metric.label}
              </h3>
              <p
                className='text-2xl font-bold'
                style={{ color: '#3F5265', fontSize: '28px' }}
              >
                {formatNumericValue(metric.value)}
              </p>
            </div>
            <div>
              <span
                className='flex items-center justify-center'
                style={{
                  width: '40px',
                  height: '40px',
                  backgroundColor: getBgColor(metric.key),
                  borderRadius: '8px',
                  color: getIconColor(metric.key),
                }}
              >
                {getIcon(metric.key)}
              </span>
            </div>
          </div>
        ))}
      </div>

      {/* Grid layout for 3 cards - Date/Time Metrics */}
      <div className='grid grid-cols-3 gap-4 mt-4'>
        {dateMetricsData.map((metric) => (
          <div
            key={metric.key}
            className='bg-white rounded-lg p-4 flex items-center justify-between border border-gray-200 shadow-sm hover:shadow-md transition-shadow duration-200'
          >
            <div className="flex-1">
              <h3
                className='text-gray-500 text-sm mb-2 font-semibold'
                style={{ color: '#657584', fontSize: '14px' }}
              >
                {metric.label}
              </h3>
              <p
                className='text-base font-medium'
                style={{ color: '#3F5265', fontSize: '16px' }}
              >
                {formatDateTime(metric.value)}
              </p>
            </div>
            <div>
              <span
                className='flex items-center justify-center'
                style={{
                  width: '40px',
                  height: '40px',
                  backgroundColor: getBgColor(metric.key),
                  borderRadius: '8px',
                  color: getIconColor(metric.key),
                }}
              >
                {getIcon(metric.key)}
              </span>
            </div>
          </div>
        ))}
      </div>
    </>
  );
};

export default AudienceViewTopCards;